import { reverse } from "@reactivated";

import { OrderStatus, PaymentState } from "../../../constants";
import { strings } from "../../../localization";
import { IPaymentRejectMsgs, IPaymentStates } from "../../../models/checkout";
import { isoWebPageURL } from "../../../models/nominals";
import { objectKeys } from "../../../utils/functional";
import { getPageSetting } from "../../../utils/settings";
import { navigateToURL } from "../../../utils/urls";
import { PRIMARY_PAYMENT_METHOD_KEY } from "../constants";
import { IPayment, IReduxState } from "../reducers.interfaces";
import { ICheckoutErrorBody, ICheckoutHTTPError } from "../types";

export const PAYMENT_REJECT_MSGS: IPaymentRejectMsgs = {
    "cybersource": strings.get("REJECT_MSG_CYBERSOURCE") || "",
    "cybersource-microform": strings.get("REJECT_MSG_CYBERSOURCE") || "",
    "bluefin": strings.get("REJECT_MSG_CYBERSOURCE") || "",
    "financing": strings.get("REJECT_MSG_FINANCING") || "",
    "cash": strings.get("REJECT_MSG_CASH") || "",
    "pay-later": strings.get("REJECT_MSG_CASH") || "",
};

/**
 * Build checkout error object.
 */
export const buildCheckoutError = (
    errorKey: keyof ICheckoutErrorBody,
    msg: string,
): ICheckoutHTTPError => {
    const error: ICheckoutHTTPError = Object.assign(new Error(msg), {
        response: {
            body: {
                [errorKey]: [msg],
            },
        },
    });
    return error;
};

// Navigation away from checkout is terminal for GA purchase-events,
// so forward at most once.
let hasForwarded = false;

/**
 * Navigate to the next page after checkout.
 * If NPS survey is enabled, redirect to the survey page first;
 * otherwise go straight to the thank-you page.
 */
export const forwardToThankYouPage = (): void => {
    if (hasForwarded) {
        return;
    }
    hasForwarded = true;
    const npsSurveyEnabled = getPageSetting("enable_nps_survey");
    if (npsSurveyEnabled) {
        navigateToURL(isoWebPageURL.wrap(reverse("checkout:nps-survey")));
    } else {
        navigateToURL(isoWebPageURL.wrap(reverse("checkout:thank-you")));
    }
};

/**
 * Test-only: clear the forward-once guard between test cases.
 */
export const resetForwardToThankYouPageGuard = (): void => {
    hasForwarded = false;
};

// Statuses at or beyond successful authorization. An order re-checked
// while Pending may have already advanced through the sync/ERP pipeline.
// Backend submission failures are included: payment has been captured by
// then, so the customer must not be shown a retry-inducing failure page.
const FORWARDABLE_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set([
    OrderStatus.AUTHORIZED,
    OrderStatus.MANUAL_REVIEW,
    OrderStatus.SYNC_SUBMITTED,
    OrderStatus.SYNC_ACKD,
    OrderStatus.ERP_SUBMITTED,
    OrderStatus.ERP_ACKD,
    OrderStatus.SYNC_SUBMIT_FAILED,
    OrderStatus.SQS_SUBMIT_FAILED,
    OrderStatus.ERP_SUBMIT_FAILED,
    OrderStatus.SHIPPED,
    OrderStatus.DELIVERED,
]);

/**
 * Check if payment states indicate the order is complete (or complete
 * enough to forward the customer to the thank-you page).
 */
export const isOrderCompleteOrForwardable = (
    states: IPaymentStates,
): boolean => {
    return (
        FORWARDABLE_ORDER_STATUSES.has(states.order_status) ||
        states.payment_method_states[PRIMARY_PAYMENT_METHOD_KEY]?.status ===
            PaymentState.DEFERRED
    );
};

/**
 * Find the method type of a declined payment method, or null if no
 * configured payment method was declined.
 */
export const findDeclinedMethodType = (
    states: IPaymentStates,
    paymentMethods: IReduxState["form"]["payment_methods"],
): IPayment["method_type"] | null => {
    for (const key of objectKeys(states.payment_method_states)) {
        const paymentMethodState = states.payment_method_states[key];
        const paymentMethodType = paymentMethods[key]?.method_type;
        if (
            paymentMethodType &&
            paymentMethodState.status === PaymentState.DECLINED
        ) {
            return paymentMethodType;
        }
    }
    return null;
};
